import React, { useState } from 'react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import { Trash2, ArrowLeft, X, ShieldCheck, CloudOff } from 'lucide-react';

const CashbookDetails = ({ entry, onDelete, onEdit, onClose }) => {
    const { t } = useLang();
    const [showFullImage, setShowFullImage] = useState(false);

    if (!entry) return null;

    const isOut = entry.type === 'out' || entry.entry_type === 'out';
    const themeColor = isOut ? '#DC2626' : '#16A34A';
    const sign = isOut ? '−' : '+';
    const amount = parseFloat(entry.amount || 0);

    const formatCurrency = (val) => {
        return '₹ ' + new Intl.NumberFormat('en-IN').format(Math.abs(val));
    };

    return (
        <div style={styles.container}>
            {/* Header */}
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <button style={styles.backBtn} onClick={onClose}>
                        <ArrowLeft size={20} />
                    </button>
                    <h2 style={styles.headerTitle}>{t(LangKeys.entry_details)}</h2>
                </div>

                <div style={styles.headerActions}>
                    <button style={styles.iconAction} onClick={() => onDelete(entry.id)}>
                        <Trash2 size={18} color="#DC2626" />
                    </button>
                </div>
            </div>

            <div style={styles.content}>
                {/* Main Amount Card */}
                <div style={styles.mainCard}>
                    <div style={styles.cardRow}>
                        <div style={styles.leftInfo}>
                            <div style={{ ...styles.typeCircle, backgroundColor: themeColor }}>
                                {sign}
                            </div>
                            <div style={styles.typeTextGroup}>
                                <div style={styles.typeLabel}>
                                    {isOut ? t(LangKeys.out_entry) : t(LangKeys.in_entry)}
                                </div>
                                <div style={styles.dateLabel}>
                                    {new Date(entry.date || entry.entry_date).toLocaleString('en-IN', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: true
                                    })}
                                </div>
                            </div>
                        </div>

                        <div style={{ ...styles.amountText, color: themeColor }}>
                            {sign} {formatCurrency(amount)}
                        </div>
                    </div>

                    <div style={styles.divider} />

                    <button style={styles.editBtn} onClick={() => onEdit(entry)}>
                        ✏️ {t(LangKeys.edit_entry)}
                    </button>
                </div>

                {/* Note Section */}
                {entry.note && (
                    <div style={styles.sectionCard}>
                        <label style={styles.sectionLabel}>{t(LangKeys.details)}</label>
                        <p style={styles.sectionContent}>{entry.note}</p>
                    </div>
                )}

                {/* Bill Image Section */}
                {entry.bill_image && (
                    <div style={styles.sectionCard}>
                        <label style={styles.sectionLabel}>{t(LangKeys.photo_max_4mb)}</label>
                        <div
                            style={styles.imagePreview}
                            onClick={() => setShowFullImage(true)}
                        >
                            <img
                                src={entry.bill_image}
                                alt="bill"
                                style={styles.billImg}
                            />
                        </div>
                    </div>
                )}

                {/* Sync Status */}
                <div style={styles.statusCard}>
                    <div style={{
                        ...styles.statusIconBox,
                        backgroundColor: entry.is_unsynced ? '#FFF7ED' : '#DCFCE7'
                    }}>
                        {entry.is_unsynced
                            ? <CloudOff size={20} color="#EA580C" />
                            : <ShieldCheck size={20} color="#16A34A" />
                        }
                    </div>

                    <div style={styles.statusTextGroup}>
                        <div style={{
                            ...styles.statusMain,
                            color: entry.is_unsynced ? '#EA580C' : '#1E293B'
                        }}>
                            {entry.is_unsynced
                                ? t(LangKeys.entry_waiting_to_be_synced)
                                : t(LangKeys.entry_is_backed_up)
                            }
                        </div>
                        {!entry.is_unsynced && (
                            <div style={styles.statusSub}>{t(LangKeys.safe_and_secure)}</div>
                        )}
                    </div>

                    {!entry.is_unsynced && (
                        <ShieldCheck size={24} color="#16A34A" style={{ marginLeft: 'auto' }} />
                    )}
                </div>
            </div>

            {/* Bottom Delete Button */}
            <div style={styles.bottomArea}>
                <button
                    style={styles.deleteFullBtn}
                    onClick={() => onDelete(entry.id)}
                >
                    🗑️ {t(LangKeys.delete).toUpperCase()}
                </button>
            </div>

            {/* Full Screen Image Modal */}
            {showFullImage && (
                <div style={styles.imageOverlay} onClick={() => setShowFullImage(false)}>
                    <div style={styles.imageModalHeader}>
                        <button
                            style={styles.closeImageBtn}
                            onClick={() => setShowFullImage(false)}
                        >
                            <X size={28} color="#FFF" />
                        </button>
                    </div>
                    <div style={styles.imageFullContent}>
                        <img
                            src={entry.bill_image}
                            alt="bill full"
                            style={styles.fullBillImg}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

const styles = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: '#F8FAFC',
        position: 'relative'
    },
    header: {
        padding: '12px 16px',
        borderBottom: '1px solid #F1F5F9',
        backgroundColor: '#FFF',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    backBtn: {
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        padding: '4px',
        display: 'flex',
        alignItems: 'center',
        color: '#64748B'
    },
    headerTitle: {
        fontSize: '16px',
        fontWeight: '700',
        color: '#1E293B',
        margin: 0
    },
    headerActions: {
        display: 'flex',
        gap: '8px'
    },
    iconAction: {
        width: '32px',
        height: '32px',
        borderRadius: '6px',
        border: '1px solid #F1F5F9',
        backgroundColor: '#FFF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer'
    },
    content: {
        flex: 1,
        padding: '20px',
        overflowY: 'auto'
    },
    mainCard: {
        backgroundColor: '#FFF',
        borderRadius: '16px',
        padding: '16px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
        marginBottom: '16px'
    },
    cardRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    leftInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    typeCircle: {
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        color: '#FFF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '20px',
        fontWeight: '800'
    },
    typeTextGroup: {
        display: 'flex',
        flexDirection: 'column'
    },
    typeLabel: {
        fontSize: '14px',
        fontWeight: '800',
        color: '#1E293B'
    },
    dateLabel: {
        fontSize: '12px',
        color: '#94A3B8'
    },
    amountText: {
        fontSize: '20px',
        fontWeight: '900'
    },
    divider: {
        height: '1px',
        backgroundColor: '#F1F5F9',
        margin: '16px 0'
    },
    editBtn: {
        width: '100%',
        border: 'none',
        background: 'none',
        color: '#2563EB',
        fontWeight: '800',
        fontSize: '14px',
        cursor: 'pointer'
    },
    sectionCard: {
        backgroundColor: '#FFF',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '12px',
        border: '1px solid #F1F5F9'
    },
    sectionLabel: {
        fontSize: '11px',
        color: '#94A3B8',
        fontWeight: '700',
        textTransform: 'uppercase',
        display: 'block',
        marginBottom: '6px'
    },
    sectionContent: {
        fontSize: '14px',
        color: '#1E293B',
        margin: 0,
        fontWeight: '500'
    },
    imagePreview: {
        marginTop: '10px',
        cursor: 'pointer'
    },
    billImg: {
        width: '80px',
        height: '80px',
        borderRadius: '8px',
        objectFit: 'cover',
        border: '1px solid #F1F5F9'
    },
    statusCard: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '14px',
        borderRadius: '12px',
        backgroundColor: '#FFF',
        border: '1px solid #F1F5F9',
        boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
    },
    statusIconBox: {
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    statusTextGroup: {
        display: 'flex',
        flexDirection: 'column'
    },
    statusMain: {
        fontSize: '13px',
        fontWeight: '700'
    },
    statusSub: {
        fontSize: '11px',
        color: '#16A34A',
        fontWeight: '600'
    },
    bottomArea: {
        padding: '16px',
        backgroundColor: '#F8FAFC'
    },
    deleteFullBtn: {
        width: '100%',
        padding: '14px',
        borderRadius: '14px',
        backgroundColor: '#FFF',
        border: '2px solid #DC2626',
        color: '#DC2626',
        fontSize: '16px',
        fontWeight: '900',
        cursor: 'pointer'
    },
    imageOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.9)',
        zIndex: 3000,
        display: 'flex',
        flexDirection: 'column'
    },
    imageModalHeader: {
        padding: '20px',
        display: 'flex',
        justifyContent: 'flex-end'
    },
    closeImageBtn: {
        border: 'none',
        background: 'none',
        cursor: 'pointer'
    },
    imageFullContent: {
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px'
    },
    fullBillImg: {
        maxWidth: '90%',
        maxHeight: '90%',
        objectFit: 'contain',
        borderRadius: '8px',
        boxShadow: '0 0 40px rgba(0,0,0,0.5)'
    }
};

export default CashbookDetails;